import React, { useState, useEffect } from 'react';
import axios from '../../api/axios';
import { useNavigate } from 'react-router-dom';
import './ContactSales.css';
import Toast from '../../ui/Toast';
import { useAuth } from '../../contexts/KeycloakContext';
import { useWorkspace } from '../../contexts/WorkspaceContext';
import PhoneInput from 'react-phone-input-2';
import 'react-phone-input-2/lib/style.css';
import countriesData from '../../data/countriesData.json';

const ContactSales = () => {
  const navigate = useNavigate();
  const { keycloak } = useAuth();
  const { primaryWorkspace } = useWorkspace();

  const [formData, setFormData] = useState({
    firstName: keycloak?.tokenParsed?.given_name || '',
    lastName: keycloak?.tokenParsed?.family_name || '',
    workEmail: keycloak?.tokenParsed?.email || '',
    country: primaryWorkspace?.country || '',
    phoneNumber: primaryWorkspace?.phoneNumber || '',
    reason: 'General inquiry',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [toast, setToast] = useState({ show: false, message: '', type: 'error' });

  useEffect(() => {
    if (primaryWorkspace) {
      setFormData((prev) => ({
        ...prev,
        country: primaryWorkspace.country || prev.country,
        phoneNumber: primaryWorkspace.phoneNumber || prev.phoneNumber,
      }));
    }
  }, [primaryWorkspace]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const response = await axios.post('/contact/sales', formData);
      if (response.data.success) {
        setToast({
          show: true,
          message: 'Your message has been sent successfully. Our team will contact you shortly.',
          type: 'success',
        });
        // Reset form
        setFormData({
          firstName: '',
          lastName: '',
          workEmail: '',
          phoneNumber: '',
          reason: 'General inquiry',
        });
      } else {
        setToast({
          show: true,
          message: 'Failed to send message. Please try again.',
          type: 'error',
        });
      }
    } catch (error) {
      console.error('Error submitting form:', error);
      setToast({ show: true, message: 'Failed to send message. Please try again.', type: 'error' });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="contact-sales-container">
      <div className="contact-sales-content">
        <div className="contact-left">
          <h1>
            You know your business.
            <br />
            We know how to scale it up.
          </h1>
          <p className="main-desc">
            Fpgberhad handles procurement and project execution with precision; missing timely,
            accurate contact data slows deals.
          </p>

          <h3 className="close-faster">Close faster with Fpgberhad</h3>

          <ul className="advantages-list">
            <li>
              <i
                className="fas fa-check-square"
                style={{ color: '#0ea5e9', marginRight: '10px', fontSize: '18px' }}
              ></i>
              <span>
                <b>Fpgberhad Advantage:</b> best data, real signals, automation
              </span>
            </li>
            <li>
              <i
                className="fas fa-check-square"
                style={{ color: '#0ea5e9', marginRight: '10px', fontSize: '18px' }}
              ></i>
              <span>
                <b>Persona Advantage:</b> empower sales to hit ICP faster
              </span>
            </li>
            <li>
              <i
                className="fas fa-check-square"
                style={{ color: '#0ea5e9', marginRight: '10px', fontSize: '18px' }}
              ></i>
              <span>
                <b>Fpgberhad Competitive Edge:</b> outpace rivals, act first
              </span>
            </li>
          </ul>
        </div>

        <div className="contact-right">
          <div className="form-card">
            <div className="form-header">
              <button className="back-btn" onClick={() => navigate('/subscription')}>
                <i className="fas fa-arrow-left"></i>
              </button>
              <h2>Contact our sales team</h2>
            </div>
            <form onSubmit={handleSubmit}>
              <div className="form-row">
                <div className="form-group">
                  <label>First name</label>
                  <input
                    type="text"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    required
                    placeholder="Jane"
                  />
                </div>
                <div className="form-group">
                  <label>Last name</label>
                  <input
                    type="text"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    required
                    placeholder="Doe"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Work email</label>
                <input
                  type="email"
                  name="workEmail"
                  value={formData.workEmail}
                  onChange={handleChange}
                  required
                  placeholder="jane.doe@company.com"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Country</label>
                  <select name="country" value={formData.country} onChange={handleChange} required>
                    <option value="">Select your country</option>
                    {countriesData.map((c) => (
                      <option key={c.code} value={c.name}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Phone number</label>
                  <PhoneInput
                    country={
                      formData.country
                        ? countriesData
                            .find((c) => c.name === formData.country)
                            ?.code?.toLowerCase() || 'us'
                        : 'us'
                    }
                    value={formData.phoneNumber}
                    onChange={(phone) => setFormData((prev) => ({ ...prev, phoneNumber: phone }))}
                    buttonContent={
                      <div className="dial-code-content">
                        +
                        {countriesData.find(
                          (c) =>
                            c.code?.toLowerCase() ===
                            (formData.country
                              ? countriesData
                                  .find((C) => C.name === formData.country)
                                  ?.code?.toLowerCase()
                              : 'us')
                        )?.dialCode || '1'}
                      </div>
                    }
                    inputProps={{
                      name: 'phoneNumber',
                      id: 'phoneNumber',
                      disabled: isSubmitting,
                    }}
                    containerClass="contact-sales-phone"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Reason of contacting</label>
                <select name="reason" value={formData.reason} onChange={handleChange}>
                  <option value="General inquiry">General inquiry</option>
                  <option value="Enterprise pricing">Enterprise pricing</option>
                  <option value="Technical support">Technical support</option>
                  <option value="Partnership">Partnership</option>
                </select>
              </div>

              <button type="submit" className="submit-btn" disabled={isSubmitting}>
                {isSubmitting ? 'Sending...' : 'Submit'}
              </button>

              <p className="terms-text">
                By clicking 'Submit' or signing up, you agree to the{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer">
                  Terms of Use
                </a>{' '}
                and{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </a>
                . You also agree to receive information and offers relevant to our services via
                email and SMS, and you may opt-out at any time. This site is protected by reCAPTCHA
                and the Google{' '}
                <a href="/privacy" target="_blank" rel="noopener noreferrer">
                  Privacy Policy
                </a>{' '}
                and{' '}
                <a href="/terms" target="_blank" rel="noopener noreferrer">
                  Terms of Service
                </a>
                .
              </p>
            </form>
          </div>
        </div>
      </div>
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={() => setToast({ ...toast, show: false })}
        />
      )}
    </div>
  );
};

export default ContactSales;
